import React, { useState, useEffect, useRef } from 'react';
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  Reroll,
  Armor,
  SkillAsAbility,
  CharacterSheetInterface,
  CharacterSpell,
  Attack,
  CharacterItem,
  CharacterWeapon,
  ExtraEffectsFeature,
  ExtraEffectsOfTypeFeature,
  CharacterAction,
  CharacterExtraEffects,
  CharacterAmmunition,
  RolledValue,
  StoredSpell,
  SKILLS,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBox";

import Roller from "@dndComponents/other/Roller";
import CharacterSkillInput from "@dndComponents/character/skills/CharacterSkillInput";
import CharacterEffect from "@dndComponents/character/spells_and_abilities/effects/CharacterEffect";
import CharacterCastButton from "@dndComponents/character/spells_and_abilities/CharacterCastButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import ImageFromString from "@dndComponents/display/ImageFromString";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import { SpellLevel } from '@dndModels/Types';

interface Props {
  char: Character;
  char_interface: CharacterSheetInterface;
  obj: CharacterAbility | CharacterSpell | StoredSpell | CharacterItem | Attack;
  action: CharacterAction;
  processing?: boolean;
  disabled: boolean;
  shared: boolean;
  level: SpellLevel;
  wide?: boolean;
  log_it?: boolean;
  show_extra_effects?: boolean;
  include_cast_button?: boolean;
  casting_time_cost?: boolean;
  attack_mod?: number;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
  onChange: (change_types: string[]) => void; // For slots, resources, and concentration
  // onCostChange: (costs: { [resource: string]: number }) => void;
  // onTransform: () => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterEffects({
  char,
  char_interface,
  obj,
  action,
  processing = false,
  disabled,
  shared,
  log_it = false,
  level,
  wide = false,
  show_extra_effects = false,
  include_cast_button = false,
  casting_time_cost = false,
  attack_mod,
  casting_time,
  onChange,
  // onTransform
}: Props) {
  // const [reloading, setReloading] = useState(false);
  const [reloading_costs, setReloadingCosts] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [popoverAttack, setPopoverAttack] = useState<Attack | null>(null);
  const [popoverMode, setPopoverMode] = useState("null");
  const [is_processing, setProcessing] = useState(false);
  const [changed_costs, setChangedCosts] = useState<{ [resource: string]: number }>({});
  const [extra_details, setExtraDetails] = useState<CharacterExtraEffects | null>(null);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  // useEffect(() => {
  //   if (reloading) {
  //     setReloading(false);
  //   }
  // }, [reloading, setReloading]);
  
  useEffect(() => {
    if (reloading_costs) {
      setReloadingCosts(false);
    }
  }, [reloading_costs, setReloadingCosts]);
  
  const enabled = shared || (!disabled && api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id)));
    
  const renderExtras = () => {
    return [
      <Popover key="rolls"
        open={ popoverAnchorEl !== null && popoverMode !== "Cast" }
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setPopoverAnchorEl(null);
          setPopoverAttack(null);
          setPopoverMode("");
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        { renderRoll() }
      </Popover>,
      <ModalOrDrawer key="extra_details"
        open={extra_details !== null}
        onClose={() => { 
          setExtraDetails(null); 
          char.subscription_holder.modalClosed();
        }}
      >
        <>
          { extra_details &&
            <Grid container spacing={0} direction="row">
              {/* <Grid item xs={12} className="header centered">{ extra_details.name }</Grid> */}
              {/* <Grid item xs={12}>
                <DisplayStringWithReplacements display_string={extra_details.description} />
              </Grid> */}
              { extra_details.char_features.map((cf, key) => {
                return (
                  <Grid
                    key={key}
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      className="header centered"
                      size={{
                        xs: 12
                      }}>{ cf.name }</Grid>
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <DisplayStringWithReplacements display_string={cf.description} />
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          }
        </>
      </ModalOrDrawer>
    ];
  }

  const renderCost = (use_action: CharacterAbility | CharacterSpell | StoredSpell | CharacterItem | Attack | CharacterExtraEffects | CharacterAction) => {
    // let l = false;
    // if (!(obj instanceof Attack)) {
    //   // console.log(obj.name);
    //   // console.log(obj.true_id);
    //   if (obj.name.includes("Divine Smite")) {
    //     // debugger;
    //     console.log("Bad");
    //     console.log(obj);
    //     console.log(action);
    //     // l = !action.special_resource;
    //   }
    // } 
    
    if (casting_time_cost) {
      return action.casting_time;
    } else {
      const action_type = use_action instanceof CharacterAction ? use_action.type : action.type;
      const action_level = use_action instanceof CharacterAction ? use_action.level : action.level;
      const the_action = use_action instanceof CharacterAction ? use_action.action : use_action;
      const ritual_only = use_action instanceof CharacterAction ? use_action.ritual_only : false;
      if (the_action instanceof CharacterAbility) {
        return (
          <CharacterCastButton
            obj={the_action}
            action={action}
            type={action_type}
            level={action_level}
            simple
            character={char}
            char_interface={char_interface}
            ritual={ritual_only}
            changed_costs={changed_costs}
            disabled={disabled && !shared}
            shared={shared}
            // log_it={l}
            // log_it={log_it}
            // log_it={use_action instanceof CharacterAbility && use_action.name.includes("Divine Smite")}
            // onChange={async () => {
            //   await char_util.resourcesChanged(char);
            // }}
          />
        );
      } else if (the_action instanceof CharacterSpell) {
        const spell = the_action;
        const the_level = level === -1 ? spell.level : level;
        let level2 = the_level;
        if (level2 > 0) {
          const slots = char_interface.use_slots.filter(o => o.level === the_level);
          if (slots.length === 0) {
            level2 = 9; 
            char_interface.use_slots.filter(o => o.level > the_level).forEach(s => {
              if (s.level < level2) {
                level2 = s.level;
              }
            });
          }
        }
        return (
          <CharacterCastButton
            obj={spell}
            action={action}
            character={char}
            char_interface={char_interface}
            level={level2}
            ritual={ritual_only}
            simple
            changed_costs={changed_costs}
            disabled={disabled && !shared}
            shared={shared}
            // log_it={spell.name.includes('Cure')}
            // onChange={async () => {
            //   await char_util.resourcesChanged(char);
            // }}
          />
        );
      } else if (the_action instanceof StoredSpell) {
        return (
          <CharacterCastButton
            obj={the_action}
            action={action}
            character={char}
            char_interface={char_interface}
            level={the_action.level}
            ritual={ritual_only}
            simple
            changed_costs={changed_costs}
            disabled={disabled && !shared}
            shared={shared}
            // log_it={spell.name.includes('Cure')}
            // onChange={async () => {
            //   await char_util.resourcesChanged(char);
            // }}
          />
        );
      } else if (the_action instanceof CharacterExtraEffects) {
        // debugger;
        if (["","None"].includes(the_action.resource_consumed)) {
          // console.log(the_action);
          return (
            <Grid container spacing={0} direction="row" 
              className="clickable" onClick={() => {
                setExtraDetails(the_action);
              }}>
              { the_action.name !== "" && 
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { the_action.name }
                </Grid>
              }
              { the_action.time_restriction !== "Hide" && 
                <Grid
                  size={{
                    xs: 12
                  }}>
                  { (the_action.time_restriction === "Special" ? "" : the_action.time_restriction) + (the_action.extra_restriction === "" ? "" : ` ${the_action.extra_restriction}`) }
                </Grid> 
              }
            </Grid>
          );
        } else {
          // console.log(the_action);
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }} style={{ width: "100%" }}>
                <CharacterCastButton
                  obj={the_action}
                  action={action}
                  type={action_type}
                  level={action_level}
                  simple
                  character={char}
                  char_interface={char_interface}
                  ritual={ritual_only}
                  changed_costs={changed_costs}
                  disabled={disabled && !shared}
                  shared={shared}
                  // log_it={log_it} 
                  // onChange={async () => {
                  //   await char_util.resourcesChanged(char);
                  // }}
                />
              </Grid>
              { the_action.name !== "" && the_action.effect_cfs.length > 1 &&
                <Grid
                  className="small_label gray clickable"
                  onClick={() => {
                    setExtraDetails(the_action);
                  }}
                  size={{
                    xs: 12
                  }}>
                  { the_action.name }
                </Grid>
              }
              { the_action.time_restriction !== "Hide" && 
                <Grid
                  className="small_label gray clickable"
                  onClick={() => {
                    setExtraDetails(the_action);
                  }}
                  size={{
                    xs: 12
                  }}>
                  { (the_action.time_restriction === "Special" ? "" : the_action.time_restriction) + (the_action.extra_restriction === "" ? "" : ` ${the_action.extra_restriction}`) }
                </Grid> 
              }
            </Grid>
          );
        }
      } else {
        if (the_action instanceof CharacterWeapon && the_action.ammunition_type !== "None" && the_action.ammunition_type !== "Special") {
          const ammos = char.ammunitions.filter(o => 
            o.ammunition_type === the_action.ammunition_type && 
            o.count > 0);
          if (ammos.length === 0) {
            return (
              <DandDButtonBox
                name={ `0 ${the_action.ammunition_type}s` }
                disabled
                onClick={() => {}}
              />
            );
          } else {
            const ammo_ids = ammos.map(o => o.pseudo_item_id);
            // for (const ammo of ammos) {
            //   console.log(ammo);
            // }
            const done_ammos: string[] = [];
            return (
              <Grid container spacing={0} direction="row" style={{ width: "100%" }}>
                { ammo_ids.map((ammo_id, key) => {
                  const ammo = ammos.find(o => o.pseudo_item_id === ammo_id);
                  if (key === 0 || show_extra_effects) {
                    if (ammo && !done_ammos.includes(ammo_id)) {
                      done_ammos.push(ammo_id);
                      const count = ammos.filter(o => o.pseudo_item_id === ammo_id).reduce((accumulator: number, char_item: CharacterItem) => {
                        return accumulator + char_item.count;
                      }, 0);

                      return (
                        <Grid
                          key={key}
                          size={{
                            xs: 12
                          }} style={{ width: "100%" }}>
                          <DandDButtonBox
                            name={ `${count} ${ammo.name}` }
                            disabled={disabled}
                            onClick={async () => {
                              // debugger;
                              const ammo2 = char.items.find(o => 
                                o.true_id === ammo.true_id);
                              if (ammo2 instanceof CharacterAmmunition) {
                                ammo2.count--;
                                if (ammo2.count === 0) {
                                  connect_util.removeItem(char, ammo2);
                                }
                                await char_util.resourcesChanged(char);
                              }
                              // char.subscription_holder.recalculate();
                            }}
                          />
                        </Grid>
                      );
                    }
                  }
                  return null;
                })}
              </Grid>
            );
          }
        }
        return "";
      }
    }
  }

  const renderSpecialCastingTime = () => {
    if (obj instanceof CharacterAbility || obj instanceof CharacterSpell) {
      if (obj.casting_times.includes("Special") && obj.special_casting_time !== "") {
        return (
          <Grid
            className='small_label gray centered'
            size={{
              xs: 12
            }}>Casting Time: { obj.special_casting_time }</Grid>
        );
      }
    }
    return null;
  }

  const renderExtraEffects = () => {
    if (show_extra_effects && 
      (obj instanceof CharacterWeapon || 
        obj instanceof CharacterAbility || 
        obj instanceof CharacterSpell || 
        obj instanceof Attack)) {
      // , log_it: action.name.includes("Delerium Orb") && action.level === 5
      const extra_effects = char_util.getExtraEffects({ char_int: char_interface, char: char, obj, action }); // , log_it: true
      // console.log(extra_effects);

      const main_damage_type = obj.main_damage_type;
      return (
        <>
          { extra_effects.map(cee => {
            // console.log(cee);
            const cfs = char_util.getCFSForCF(char, cee.main_cf);
            const colors = cfs ? char_util.getColors({ char, user: api.user, for_id: cfs.source_id }) : undefined;
            const styles: React.CSSProperties = colors ? { ...colors, border: "1px solid" } : { border: "1px solid" };
            if (styles && styles.backgroundColor) {
              let bg = styles.backgroundColor.toString();
              if (bg.startsWith("rgba(") && bg.includes("0.8)")) {
                bg = bg.replace("0.8)", "1)");
              } else if (bg.startsWith("#") && bg.length === 9) {
                bg = bg.substring(0, 7);
              }
              styles.backgroundColor = bg;
            }
            return (
              <Grid
                key={cee.true_id}
                container
                spacing={0}
                direction="row"
                style={styles}
                size={12}>
                <Grid
                  className="centered"
                  size={3}>
                  { renderCost(cee) }
                </Grid>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 9
                  }}>
                  { cee.effect_cfs.map(cf => {
                    // debugger;
                    // console.log(cf);
                    return (
                      <Grid
                        key={cf.true_id}
                        container
                        spacing={0}
                        direction="row"
                        size={{
                          xs: 12
                        }}>
                        { (cf.floating_feature instanceof ExtraEffectsFeature || 
                          cf.floating_feature instanceof ExtraEffectsOfTypeFeature) && 
                          cf.floating_feature.effects.map(effect => {
                          // if (cf.name.includes("Disappearing")) {
                          //   console.log(effect);
                          // }
                          // console.log(effect);
                          return (
                            <Grid key={effect.true_id} size={12}>
                              <CharacterEffect
                                // spell={spell2}
                                main_damage_type={main_damage_type}
                                effect={effect}
                                action={action}
                                character={char}
                                char_interface={char_interface}
                                level={ cee._slot_level === -1 ? level : cee._slot_level }
                                wide={wide}
                                attack_mod={attack_mod}
                                casting_time={casting_time}
                                processing={processing}
                                disabled={!enabled}
                                shared={shared}
                                show_extra_effects={show_extra_effects}
                                // log_it={cf.name.includes("Disappearing")}
                                ee_cf={cf}
                                cee={cee}
                                hide_touch
                                type_cf={ cee.main_cf.true_id === cf.true_id ? undefined : cee.main_cf }
                                onChange={onChange}
                                onCostChange={(costs: { [resource: string]: number }) => {
                                  setChangedCosts(costs);
                                  setReloadingCosts(true);
                                }}
                                // onTransform={onTransform}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            );
          })}
        </>
      );
    }
    return null;
  }

  const renderEffects = () => {
    // const minimum_roll = spell instanceof CharacterAbility ? char_util.get_minimum_roll(this.props.character, "Attack", spell.attack) : 0;
    let return_me: any = null;
    if (obj instanceof CharacterAbility && 
      obj.the_ability instanceof SkillAsAbility) {
      const range_string = char_util.getRangeString({ char_int: char, spell: obj });
      return_me = (
        <Grid container spacing={0} direction="row">
          <Grid className="centered" size={{ xs: 3 }}>
            { casting_time_cost ? action.casting_time : 
              <Grid container spacing={0} direction="row">
                <Grid size={{ xs: 12 }}>{ renderCost(obj) }</Grid>
                { renderSpecialCastingTime() }
                {/* { renderDisabled() } */}
              </Grid>
            }
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 3
            }}>
            { range_string }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={6}>
            { obj.the_ability.skill_ids.map((skill_id, key) => {
              const skill = SKILLS.find(o => o._id === skill_id);
              if (skill && (key === 0 || show_extra_effects)) {
                return (
                  <Grid size={12} key={key}>
                    <CharacterSkillInput
                      character={char}
                      char_interface={char_interface}
                      skill={skill}
                    />
                  </Grid>
                );
              }
              return null;
            })}
          </Grid>
        </Grid>
      );
    } else if (obj instanceof CharacterItem) { 
      if (obj instanceof CharacterWeapon) {
        const weapon = obj;
        // if (weapon.name.includes("Venomous")) {
        //   debugger;
        // }
        const filtered = weapon.attacks.filter(o => (casting_time !== "BA" && !o.bonus_action) || (casting_time === "BA" && o.bonus_action));
        const { advantage, advantages, greater_advantage } = char_util.getAdvantage(char_interface, "Attack", weapon);
        
        if (filtered.length > 0) {
          return_me = (
            <Grid container spacing={0} direction="row">
              { include_cast_button &&
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 3
                  }}>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    { renderCost(obj) }
                  </Grid>
                  {/* { renderDisabled() } */}
                </Grid>
              }
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: include_cast_button ? 9 : 12
                }}>
                { filtered.map((attack, key) => {
                  const range_string = char_util.getRangeString({ char_int: char, weapon, attack });
                  if (key === 0 || show_extra_effects) {
                    return (
                      <Grid
                        key={key}
                        container
                        spacing={0}
                        direction="row"
                        size={{
                          xs: 12
                        }}>
                        <Grid
                          className="centered"
                          size={{
                            xs: 4
                          }}>
                          { range_string !== "" ?
                            <Grid container spacing={0} direction="column">
                              <Grid className="centered">
                                { attack.type }
                              </Grid>
                              <Grid className="centered">
                                { range_string }
                              </Grid>
                            </Grid>
                            :
                            attack.type
                          }
                        </Grid>
                        <Grid
                          className="centered"
                          size={{
                            xs: 4
                          }}>
                          { attack.attack_string !== "No Attack" &&
                            <DandDButtonBox
                              name={ attack.attack_string }
                              advantage={ advantage }
                              advantages={ advantages }
                              greater_advantage={ greater_advantage }
                              // minimum_roll={minimum_roll}
                              disabled={!enabled}
                              // log_it
                              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                // const { advantage } = char_util.getAdvantage(character, "Attack", obj);
                                const minimum_roll = char_util.getMinimumRoll(char_interface, "Attack", attack);
                                // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Attack")) && attack.attack_stat !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(attack.attack_stat))).length > 0;
                                setRoll(char_util.rollD20({ char_interface: char, rolls: attack.attack_rolls, type: "Attack", use_ability_score: attack.attack_stat, advantage, greater_advantage, minimum_roll }));
                                setPopoverAttack(attack);
                                setPopoverMode("Attack");
                                setPopoverAnchorEl(event.currentTarget);
                              }} 
                            />
                          }
                        </Grid>
                        <Grid
                          className="centered"
                          size={{
                            xs: 4
                          }}>
                          <DandDButtonBox
                            name={ attack.damage_string }
                            find_images
                            disabled={ !enabled }
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                              const ability = obj;
                              const weapon = ability;
                              const rerolls: Reroll[] = [];
                              // console.log(char_interface.rerolls);
                              for (const r of char_interface.rerolls) {
                                let good = false;
                                if (r.allowed_armor_types.includes("ALL")) {
                                  // Check for required
                                  if (r.required_armor_types.includes("None")) {
                                    good = true;
                                  } else if (r.required_armor_types.includes("Any")) {
                                    good = char.equipped_items.filter(o => 
                                      o.base_item &&
                                      o.base_item.item_type === "Armor" &&
                                      o.base_item.name !== "Shield"
                                    ).length > 0;
                                  } else {
                                    const missing_armor_types = r.required_armor_types.filter(a =>
                                      char.equipped_items.filter(o =>
                                        o.base_item &&
                                        o.base_item.item_type === "Armor" &&
                                        o.base_item instanceof Armor &&
                                        o.base_item.armor_type_id === a).length === 0);
                                    good = missing_armor_types.length === 0;
                                  }
                                } else if (r.allowed_armor_types.includes("None")) {
                                  good = char.equipped_items.filter(o => 
                                    o.base_item &&
                                    o.base_item.item_type === "Armor" &&
                                    o.base_item.name !== "Shield"
                                  ).length === 0;
                                } else {
                                  const bad_armor_items = char.equipped_items.filter(o =>
                                    o.base_item &&
                                    o.base_item.item_type === "Armor" &&
                                    o.base_item instanceof Armor &&
                                    !r.allowed_armor_types.includes(o.base_item.armor_type_id));
                        
                                  good = bad_armor_items.length === 0;
                                }
                                if (good) {
                                  const weapon_keyword_ids = char_util.getWeaponKeywordIds(char, weapon);
                                  // TODO: I think this part is wrong
                                  if (!r.required_weapon_keywords.includes("None")) {
                                    good = common_util.anyOverlaps(r.required_weapon_keywords, weapon_keyword_ids);
                                  }
                                  if (good) {
                                    if (!r.excluded_weapon_keywords.includes("None")) {
                                      good = !common_util.anyOverlaps(r.excluded_weapon_keywords, weapon_keyword_ids);
                                    }
                                  }
                                  if (good) {
                                    if (!r.allowed_damage_types.includes("ALL")) {
                                      good = false;
                                      for (let i = 0; i < attack.damage_rolls.length; ++i) {
                                        if (r.allowed_damage_types.includes(attack.damage_rolls[i].type)) {
                                          good = true;
                                          break;
                                        }
                                      }
                                    }
                                  }
                                  if (good) {
                                    rerolls.push(r);
                                  }
                                }
                              }
                              setRoll(char_util.rollOther({ char_interface: char, rolls: attack.damage_rolls, rerolls, type: "Damage" }));
                              setPopoverAttack(attack);
                              setPopoverMode("Damage");
                              setPopoverAnchorEl(event.currentTarget);
                            }} 
                          />
                        </Grid>
                      </Grid>
                    );
                  } else {
                    return null;
                  }
                })}
              </Grid>
              { renderExtraEffects() }
            </Grid>
          );
        }
      }
    } else if (obj instanceof Attack) {
      // debugger;
      const attack = obj;
      const { advantage, advantages, greater_advantage } = char_util.getAdvantage(char_interface, "Attack", attack);
      const range_string = char_util.getRangeString({ char_int: char, attack });

      return_me = (
        <Grid container spacing={0} direction="row">
          { include_cast_button &&
            <Grid size={{ xs: 3 }}>
              {/* { renderDisabled() } */}
            </Grid>
          }
          <Grid container spacing={0} direction="row" size={{ xs: include_cast_button ? 9 : 12 }}>
            <Grid className="centered" size={{ xs: 4 }}>
              { range_string !== "" ?
                <Grid container spacing={0} direction="column">
                  <Grid className="centered">
                    { attack.type }
                  </Grid>
                  <Grid className="centered">
                    { range_string }
                  </Grid>
                </Grid>
                :
                attack.type
              }
            </Grid>
            <Grid className="centered" size={{ xs: 4 }}>
              <DandDButtonBox
                name={ attack.attack_string }
                advantage={ advantage }
                advantages={ advantages }
                greater_advantage={ greater_advantage }
                disabled={!enabled}
                // minimum_roll={minimum_roll}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  const ability = obj;
                  // const { advantage } = char_util.getAdvantage(character, "Attack", obj);
                  const attack = ability;
                  // const greater_advantage = character.greater_advantages.filter(o => (o.roll_types.length === 0 || o.roll_types.includes("Attack")) && attack.attack_stat !== "None" && (o.use_ability_scores.length === 0 || o.use_ability_scores.includes(attack.attack_stat))).length > 0;
                  setRoll(char_util.rollD20({ char_interface: char, rolls: attack.attack_rolls, type: "Attack", use_ability_score: attack.attack_stat, advantage, greater_advantage }));
                  setPopoverMode("Attack");
                  setPopoverAnchorEl(event.currentTarget);
                }} 
              />
            </Grid>
            <Grid className="centered" size={{ xs: 4 }}>
              <Grid container spacing={0} direction="row" style={{ width: "100%" }}>
                <Grid size={{ xs: 12 }} style={{ width: "100%" }}>
                  <DandDButtonBox
                    name={ attack.damage_string }
                    find_images
                    disabled={ !enabled }
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                      const ability = obj;
                      const attack = ability;
                      setRoll(char_util.rollOther({ char_interface: char, rolls: attack.damage_rolls, type: "Damage" }));
                      setPopoverMode("Damage");
                      setPopoverAnchorEl(event.currentTarget);
                    }} 
                  />
                </Grid>
                { char.unarmed_strike_damage_types.length > 1 &&
                  <Grid size={{ xs: 12 }}>
                    { char.unarmed_strike_damage_types.map((dt, key) => {
                      return <span key={key}><ImageFromString image={dt} />&nbsp;</span>;
                    })}
                  </Grid>
                }
              </Grid>
            </Grid>
          </Grid>
          { renderExtraEffects() }
        </Grid>
      );
    } else {
      const spell2 = obj;
      if (log_it) {
        console.log(obj);
        if (!(obj instanceof Attack)) {
          console.log(spell2.effects);
        }
        // debugger;
      }
      
      return_me = (
        <Grid container spacing={0} direction="row">
          <Grid size={12} 
            container spacing={0} direction="row">
            { include_cast_button &&
              <Grid size={3} className="centered">
                { casting_time_cost ? action.casting_time : 
                  <Grid container spacing={0} direction="row" style={{ width: "100%" }}>
                    <Grid size={{ xs: 12 }} style={{ width: "100%" }}>{ renderCost(obj) }</Grid>
                    { renderSpecialCastingTime() }
                  </Grid>
                }
              </Grid>
            }
            <Grid size={{ xs: include_cast_button ? 9 : 12 }}
              container spacing={0} direction="row">
              { spell2.effects.map((effect, key) => {
                // if (show_extra_effects) {
                //   debugger;
                // }
                if (!show_extra_effects && key > 0) {
                  return null;
                }
                return (
                  <Grid size={12} key={effect.true_id}>
                    <CharacterEffect
                      spell={spell2}
                      effect={effect}
                      action={action}
                      character={char}
                      char_interface={char_interface}
                      level={level}
                      wide={wide}
                      attack_mod={attack_mod}
                      casting_time={casting_time}
                      processing={processing}
                      disabled={!enabled}
                      shared={shared}
                      show_extra_effects={show_extra_effects}
                      log_it={log_it}
                      onChange={onChange}
                      hide_touch={key > 0}
                      onCostChange={(costs: { [resource: string]: number }) => {
                        setChangedCosts(costs);
                        setReloadingCosts(true);
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
          <Grid size={12} 
            container spacing={0} direction="row">
            { show_extra_effects && action.extra_actions.map((extra_action, key) => {
              const the_extra_action = extra_action.action;
              if (the_extra_action && !(the_extra_action instanceof Attack || the_extra_action instanceof CharacterItem)) {
                // the_extra_action
                return (
                  <Grid size={12} key={key}
                    container spacing={0} direction="row"
                    style={{ border: "1px solid" }}>
                    <Grid size={3} className="centered">
                      { renderCost(extra_action) }
                    </Grid>
                    <Grid size={9}
                      container spacing={0} direction="row">
                      { the_extra_action.effects.map((effect, key) => {
                        // if (show_extra_effects) {
                        //   debugger;
                        // }
                        if (!show_extra_effects && key > 0) {
                          return null;
                        }
                        return (
                          <Grid size={12} key={effect.true_id}>
                            <CharacterEffect
                              spell={the_extra_action}
                              effect={effect}
                              action={extra_action}
                              character={char}
                              char_interface={char_interface}
                              level={extra_action.level}
                              wide={wide}
                              attack_mod={attack_mod}
                              casting_time={casting_time}
                              processing={processing}
                              disabled={!enabled}
                              shared={shared}
                              show_extra_effects={show_extra_effects}
                              hide_touch
                              // log_it={log_it}
                              onChange={onChange}
                              onCostChange={(costs: { [resource: string]: number }) => {
                                setChangedCosts(costs);
                                setReloadingCosts(true);
                              }}
                              // onTransform={onTransform}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            { renderExtraEffects() }
          </Grid>
        </Grid>
      );
    } 
    return return_me;
  }

  const renderRoll = () => {
    // debugger;
    if (popoverAnchorEl !== null && popoverMode !== "Cast") {
      const mode = popoverMode;
      const ability = obj;
      // const { advantage } = char_util.getAdvantage(character, "Attack", obj);
      if (ability) {
        if (ability instanceof Attack) {
          const attack = ability;
          if (mode === "Attack") {
            return (
              <Roller 
                name="Attack"
                rolled_values={roll} 
                type="Attack" 
              />
            );
          } else if (mode === "Damage") {
            // const rerolls: Reroll[] = [];
            return (
              <Roller 
                name={attack.type}
                rolled_values={roll} 
                type="Damage" 
              />
            );
          }
        } else if (ability instanceof CharacterWeapon) {
          // const weapon = ability;
          const attack = popoverAttack;
          if (attack) {
            if (mode === "Attack") {
              return (
                <Roller 
                  name="Attack"
                  // source={popoverWeapon}
                  // char={character}
                  // char_interface={char_interface}
                  rolled_values={roll} 
                  type="Attack" 
                />
              );
            } else if (mode === "Damage") {
              // const rerolls: Reroll[] = [];
              // if (weapon) {
              //   char_interface.rerolls.forEach(r => {
              //     let good = false;
              //     if (r.allowed_armor_types.includes("ALL")) {
              //       // Check for required
              //       if (r.required_armor_types.includes("None")) {
              //         good = true;
              //       } else if (r.required_armor_types.includes("Any")) {
              //         good = character.equipped_items.filter(o => 
              //           o.base_item &&
              //           o.base_item.item_type === "Armor" &&
              //           o.base_item.name !== "Shield"
              //         ).length > 0;
              //       } else {
              //         const missing_armor_types = r.required_armor_types.filter(a =>
              //           character.equipped_items.filter(o =>
              //             o.base_item &&
              //             o.base_item.item_type === "Armor" &&
              //             o.base_item instanceof Armor &&
              //             o.base_item.armor_type_id === a).length === 0);
              //         good = missing_armor_types.length === 0;
              //       }
              //     } else if (r.allowed_armor_types.includes("None")) {
              //       good = character.equipped_items.filter(o => 
              //         o.base_item &&
              //         o.base_item.item_type === "Armor" &&
              //         o.base_item.name !== "Shield"
              //       ).length === 0;
              //     } else {
              //       const bad_armor_items = character.equipped_items.filter(o =>
              //         o.base_item &&
              //         o.base_item.item_type === "Armor" &&
              //         o.base_item instanceof Armor &&
              //         !r.allowed_armor_types.includes(o.base_item.armor_type_id));
          
              //       good = bad_armor_items.length === 0;
              //     }
              //     if (good) {
              //       // TODO: I think this part is wrong
              //       if (!r.required_weapon_keywords.includes("None")) {
              //         for (let i = 0; i < r.required_weapon_keywords.length; ++i) {
              //           if (weapon.base_item instanceof Weapon && weapon.base_item.weapon_keyword_ids.includes(r.required_weapon_keywords[i])) {
              //             good = false;
              //             break;
              //           }
              //         }        
              //       }
              //       if (good) {
              //         if (!r.excluded_weapon_keywords.includes("None") && weapon.base_item instanceof Weapon) {
              //           for (let i = 0; i < weapon.base_item.weapon_keyword_ids.length; ++i) {
              //             if (r.excluded_weapon_keywords.includes(weapon.base_item.weapon_keyword_ids[i])) {
              //               good = false;
              //               break;
              //             }
              //           }
              //         }
              //       }
              //       if (good) {
              //         if (!r.allowed_damage_types.includes("ALL")) {
              //           good = false;
              //           for (let i = 0; i < attack.damage_rolls.length; ++i) {
              //             if (r.allowed_damage_types.includes(attack.damage_rolls[i].type)) {
              //               good = true;
              //               break;
              //             }
              //           }
              //         }
              //       }
              //       if (good) {
              //         rerolls.push(r);
              //       }
              //     }
              //   });
              // }
              return (
                <Roller 
                  name={attack.type}
                  // char={character}
                  // char_interface={char_interface}
                  rolled_values={roll} 
                  type="Damage" 
                />
              );
            }
          }
        }
      }
    }
    return null;
  }

  return (
    <>
      { renderEffects() }
      { renderExtras() }
    </>
  );
}

